import { Box, Paper, Table, Text } from "@mantine/core"
import { CheckCircleIcon, PlusIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { IPullSheetLineShortage } from "../../apis/picking-shortage"
import type { IPullSheetLine } from "../../models/PullSheetLine"
import { dateTimeSecFormat } from "../../utils/date"
import { formatQuantity } from "../../utils/format-quantity"
import { DeliveryActionButton } from "../delivery/action-controls"
import type { DeliveryActionState } from "../delivery/delivery-actions"
import { ListState } from "../ui"
import { ShortageReasonBadge } from "./atoms"

type Props = Readonly<{
  rows: IPullSheetLineShortage[]
  lines: IPullSheetLine[]
  loading: boolean
  reportState: Pick<DeliveryActionState, "allowed" | "reason">
  onReport: () => void
}>

export function ShortageList({ rows, lines, loading, reportState, onReport }: Props) {
  const { t } = useTranslation("delivery")

  if (loading) return <ListState state="loading" columns={6} rows={3} />

  if (rows.length === 0) {
    return (
      <Paper withBorder radius="md" p="xl">
        <Box ta="center">
          <Box c="green.7" mb="xs">
            <CheckCircleIcon size={32} weight="fill" />
          </Box>
          <Text fz={16} fw={700}>
            {t("shortage.emptyTitle")}
          </Text>
          <Text fz={14} c="gray.8" maw={380} mx="auto" mt={4}>
            {t("shortage.emptyText")}
          </Text>
          <DeliveryActionButton state={reportState} mt="md" variant="default" leftSection={<PlusIcon size={16} />} onClick={onReport}>
            {t("shortage.report")}
          </DeliveryActionButton>
        </Box>
      </Paper>
    )
  }

  const lineById = new Map(lines.map((l) => [l.id, l]))

  return (
    <Paper withBorder radius="md" p={0} style={{ overflowX: "auto" }}>
      <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th fz={14}>{t("shortage.cols.line")}</Table.Th>
            <Table.Th fz={14} ta="right">
              {t("shortage.cols.short")}
            </Table.Th>
            <Table.Th fz={14}>{t("shortage.cols.reason")}</Table.Th>
            <Table.Th fz={14}>{t("shortage.cols.note")}</Table.Th>
            <Table.Th fz={14}>{t("shortage.cols.photos")}</Table.Th>
            <Table.Th fz={14}>{t("shortage.cols.reportedAt")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const line = lineById.get(row.pullSheetLineId)
            const name = line ? [line.name, line.optionValue].filter(Boolean).join(", ") : "-"
            return (
              <Table.Tr key={`${row.pullSheetId}-${row.pullSheetLineId}-${row.reportedAt}`}>
                <Table.Td>
                  <Text fz={14} fw={700}>
                    {name}
                  </Text>
                  <Text fz={14} c="gray.8" ff="monospace">
                    {line?.skuCode || "-"}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text fz={14} fw={700}>
                    {formatQuantity(row.shortQuantity)} {line?.unit ?? ""}
                  </Text>
                  <Text fz={14} c="gray.8">
                    {t("shortage.loaded", { qty: formatQuantity(line?.quantity) })}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <ShortageReasonBadge reasonCode={row.reasonCode} />
                </Table.Td>
                <Table.Td>
                  <Text fz={14}>{row.reasonText || "-"}</Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={14}>{row.photoUrls?.length ? t("shortage.photoCount", { count: row.photoUrls.length }) : "-"}</Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={14} ff="monospace">
                    {dateTimeSecFormat(row.reportedAt)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Paper>
  )
}
